import { Link } from "react-router-dom";
import { useCMSPages } from "@/hooks/useCMSPages";
import { Instagram, Youtube, Mail, Phone, MessageCircle } from "lucide-react";
import logo from "@/assets/logo.png";
import { useActiveServices } from "@/hooks/useActiveServices";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { SnapchatIcon } from "@/components/icons/SnapchatIcon";

const TikTokIcon = () => (
  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export function Footer() {
  // Footer service links follow the live CMS service list
  const { services: activeServices } = useActiveServices();
  const { settings } = useSiteSettings();
  const { data: cmsPages } = useCMSPages();
  const isPageEnabled = (pageKey: string) => {
    const page = cmsPages?.find((p) => p.page_key === pageKey);
    return page ? page.is_enabled : true;
  };
  const socialLinks = [
    { href: settings.socials.instagram, icon: Instagram, label: "Instagram" },
    { href: settings.socials.snapchat, icon: null, label: "Snapchat" },
    { href: settings.socials.tiktok, icon: null, label: "TikTok" },
    { href: settings.socials.youtube, icon: Youtube, label: "YouTube" },
  ].filter((s) => !!s.href);
  const services = activeServices.map((service) => ({
    href: service.href,
    label: service.title,
  }));

  return (
    <footer className="bg-card border-t border-border relative">
      {/* Gold gradient border at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img
                src={settings.logoUrl || logo}
                alt="Himilo Studio"
                width="85"
                height="80"
                loading="lazy"
                decoding="async"
                className="h-20 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Luxury cinematic videography for life's most important moments. 
              Crafting visual stories that captivate and inspire.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-colors"
                  aria-label={social.label}
                >
                  {social.icon ? <social.icon size={18} /> : social.label === "Snapchat" ? <SnapchatIcon className="w-[18px] h-[18px]" /> : <TikTokIcon />}
                </a>
              ))}
            </div>
          </div>

          {/* Services - Two Columns */}
          <div className="lg:col-span-2">
            <h4 className="font-serif text-lg mb-6 text-foreground">Services</h4>
            <ul className="grid grid-cols-2 gap-x-8 gap-y-3">
              {services.map((service) => (
                <li key={service.href}>
                  <Link
                    to={service.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-foreground">Quick Links</h4>
            <ul className="space-y-3">
              {isPageEnabled("portfolio") && (
                <li>
                  <Link to="/portfolio" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Portfolio
                  </Link>
                </li>
              )}
              {isPageEnabled("about") && (
                <li>
                  <Link to="/about" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    About Us
                  </Link>
                </li>
              )}
              {isPageEnabled("contact") && (
                <li>
                  <Link to="/contact" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Contact
                  </Link>
                </li>
              )}
              <li>
                <Link to="/login" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Client Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-foreground">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`mailto:${settings.email}`}
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Mail size={16} />
                  {settings.email}
                </a>
              </li>
              {settings.showPhone && (
                <>
                  <li>
                    <a
                      href={settings.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Phone size={16} />
                      {settings.phone}
                    </a>
                  </li>
                  <li>
                    <a
                      href={settings.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      <MessageCircle size={16} />
                      WhatsApp
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Himilo Studio. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
